import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import CurationFilters from '@components/CurationFilters';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import DiscernAssignedUserDropdown from '@components/DiscernAssignedUserDropdown';
import DeleteDiscernButton from '@components/DiscernSummary/Component/DiscernDeleteButton';
import RetryAutoCurationButton from '@components/DiscernSummary/Component/RetryAutoCurationButton';

import { DISCERN_JOBS } from '@constants/routes';

import { useDeleteDiscernDocument } from '@hooks/api/discern';
import { useDiscernFilters } from '@hooks/useDiscernFilters';
import { useRetryAutoCuration } from '@hooks/useRetryAutoCuration';
import { useSnackbar } from '@hooks/useSnackbar';

import {
  applyClientFilters,
  buildAssignedUsersMap,
  transformDiscernDocuments,
} from '@utils/discern-transforms';
import { formatDate } from '@utils/format';

import { useListDiscernDocumentsQuery } from 'generated/discern.generated';
import { CurationStatus, RoleName } from 'generated/types';

const messages = defineMessages({
  noResources: {
    id: 'patientDiscernTable.noResources',
    defaultMessage: 'No files uploaded for this patient',
  },
  documentFailed: {
    id: 'patientDiscernTable.documentFailed',
    defaultMessage: 'This document failed to process and cannot be opened.',
  },
});

interface PatientDiscernTableProps {
  vitallPatientId: string;
  patientName: string;
}

const PatientDiscernTable: React.FC<PatientDiscernTableProps> = ({
  vitallPatientId,
  patientName,
}) => {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const POLL_INTERVAL_MS = 5000;

  // Fetch Discern documents for this patient.
  // The loading guard below keeps the table visible during polling cycles.
  const {
    serverFilters,
    clientFilters,
    setServerFilter,
    setClientFilter,
    clearAllFilters,
    toQueryInput,
    hasActiveFilters,
  } = useDiscernFilters();

  const { data, loading, startPolling, stopPolling } =
    useListDiscernDocumentsQuery({
      variables: {
        input: toQueryInput({ vitall_patient_id: vitallPatientId }),
      },
    });

  const docs = React.useMemo(
    () => data?.listDiscernDocuments ?? [],
    [data?.listDiscernDocuments]
  );

  // isInitialLoading guards ContentLoader: keep the table visible during polls.
  const isInitialLoading = loading && !data;

  const isPolling = React.useRef(false);

  const hasStructuringDocument = docs.some(
    (doc) => doc.status === CurationStatus.Structuring
  );

  React.useEffect(() => {
    if (hasStructuringDocument) {
      if (!isPolling.current) {
        startPolling(POLL_INTERVAL_MS);
        isPolling.current = true;
      }
      return () => {
        stopPolling();
        isPolling.current = false;
      };
    }
    stopPolling();
    isPolling.current = false;
  }, [hasStructuringDocument, startPolling, stopPolling]);

  const [deleteDiscernDocument] = useDeleteDiscernDocument();
  const { handleRetryDocument, retryingDocumentId } = useRetryAutoCuration();

  const handleDeleteDocument = async (id: string) => {
    await deleteDiscernDocument({ discern_document_id: id });
  };

  const assignedUsersMap = React.useMemo(
    () => buildAssignedUsersMap(docs),
    [docs]
  );
  const allRows = React.useMemo(
    () => transformDiscernDocuments(docs, patientName),
    [docs, patientName]
  );
  const tableData = React.useMemo(
    () => applyClientFilters(allRows, clientFilters),
    [allRows, clientFilters]
  );

  const availableUploaders = React.useMemo(
    () => [...new Set(allRows.map((r) => r.uploadedBy).filter(Boolean))].sort(),
    [allRows]
  );

  const handleFileClick = (
    discernDocumentId: string,
    binaryId: string | undefined,
    status: string
  ) => {
    if (status === CurationStatus.Failed) {
      enqueueSnackbar({
        message: formatMessage(messages.documentFailed),
        variant: 'error',
      });
      return;
    }
    if (
      status === 'InReview' ||
      status === 'Complete' ||
      status === 'Structuring'
    ) {
      navigate(`${DISCERN_JOBS}${discernDocumentId}`);
      return;
    }
    if (binaryId) {
      const queryParams = new URLSearchParams({
        binaryId,
        vitallPatientId: vitallPatientId,
        patientName: patientName,
        discernDocumentId: discernDocumentId,
      });
      navigate(`/discern/split-editor?${queryParams.toString()}`);
    }
  };

  return (
    <ContentLoader loading={isInitialLoading}>
      <CurationFilters
        serverFilters={serverFilters}
        clientFilters={clientFilters}
        onServerFilterChange={setServerFilter}
        onClientFilterChange={setClientFilter}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        availableUploaders={availableUploaders}
        showPatientSearch={false}
      />
      <MuiDataTable
        columns={[
          {
            name: 'filename',
            className: 'small',
            label: 'Filename',
            options: {
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <Chip
                    label={rowData.filename}
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileClick(
                        rowData.discernDocumentId,
                        rowData.s3_key || undefined,
                        rowData.status
                      );
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: palette.action.hover },
                    }}
                  />
                );
              },
              filter: true,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'uploadedBy',
            className: 'small',
            label: 'Uploaded By',
            options: { filter: true, sort: true, searchable: true },
          },
          {
            name: 'dateUploaded',
            className: 'date',
            label: 'Date Uploaded',
            options: {
              customBodyRender: (date: string) =>
                formatDate({ date, formatter: 'datetime' }),
              filter: true,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'lastUpdated',
            className: 'date',
            label: 'Last Updated',
            options: {
              customBodyRender: (date: string) =>
                formatDate({ date, formatter: 'datetime' }),
              filter: true,
              sort: true,
              searchable: true,
            },
          },
          {
            name: 'status',
            label: 'Status',
            className: 'small',
            options: {
              customBodyRender: (status: CurationStatus) => {
                return <CurationStatusBadge status={status} />;
              },
              filter: true,
              sort: true,
            },
          },
          {
            name: 'assignedUser',
            className: 'small',
            label: 'Assigned User',
            options: {
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <DiscernAssignedUserDropdown
                    name={rowData.assignedUserName}
                    value={rowData.assignedUserId}
                    discernDocumentId={rowData.discernDocumentId}
                    groupingStateJson={rowData.groupingStateJson}
                    role={RoleName.Curator}
                  />
                );
              },
              filter: true,
              sort: true,
              searchable: true,
              filterOptions: {
                renderValue: (value) => {
                  const userName =
                    value in assignedUsersMap ? assignedUsersMap[value] : null;
                  if (!userName) {
                    return '(None)';
                  }
                  return userName;
                },
              },
            },
          },
          {
            name: '',
            options: {
              customHeadLabelRender: () => '',
              customBodyRenderLite: (dataIndex) => {
                const rowData = tableData[dataIndex];
                return (
                  <Box component="span" sx={{ display: 'flex', gap: 0.5 }}>
                    <RetryAutoCurationButton
                      rowData={rowData}
                      handleRetryDocument={handleRetryDocument}
                      retrying={
                        retryingDocumentId === rowData.discernDocumentId
                      }
                    />
                    <DeleteDiscernButton
                      rowData={rowData}
                      handleDeleteDocument={handleDeleteDocument}
                    />
                  </Box>
                );
              },
            },
          },
        ]}
        data={tableData}
        options={{
          sortOrder: { name: 'dateUploaded', direction: 'desc' },
          sort: true,
          search: true,
          filter: true,
          onRowClick: (_rowData, { dataIndex }) => {
            const rowData = tableData[dataIndex];
            if (rowData.discernDocumentId) {
              handleFileClick(
                rowData.discernDocumentId,
                rowData.s3_key || undefined,
                rowData.status
              );
            }
          },
          textLabels: {
            body: { noMatch: formatMessage(messages.noResources) },
          },
        }}
        title={`Files for ${patientName}`}
      />
    </ContentLoader>
  );
};

export default PatientDiscernTable;
